import { useWorkerProfile, legacyProfileResponse } from 'src/workspaces/workerProfile/WorkerProfileContext';
// Read-only credential wallet panel on the admin Contractor Detail page (#307).
//
// Lists every credential the contractor has registered, grouped by category
// and decorated with a status pill driven by the backend's derived `status`
// (`active` / `expiring_soon` / `expired`). Admins can audit but not edit —
// per the issue's acceptance criteria, write access stays with the contractor
// in the mobile wallet.

import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useLocation } from 'react-router';

import { getContractorCredentials } from 'src/helper/backend_helper';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  CredentialStatus,
  ContractorCredential
} from './types';

interface LocationState {
  user?: {
    id?: number;
  };
}

const STATUS_COLOR: Record<
  CredentialStatus,
  'success' | 'warning' | 'error'
> = {
  active: 'success',
  expiring_soon: 'warning',
  expired: 'error'
};

const STATUS_LABEL: Record<CredentialStatus, string> = {
  active: 'Active',
  expiring_soon: 'Expiring soon',
  expired: 'Expired'
};

function formatExpiry(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function CredentialsSection() {
  const scope = useWorkerProfile();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const contractorId = scope?.workerId ?? state?.user?.id;

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['contractor_credentials', scope?.companyId, scope?.revision, contractorId],
    queryFn: () => scope ? legacyProfileResponse(scope.data.credentials) : getContractorCredentials(contractorId as number),
    select: (response): ContractorCredential[] =>
      (response?.data?.data as ContractorCredential[]) ?? [],
    enabled: typeof contractorId === 'number' && contractorId > 0
  });

  if (!contractorId) {
    return null;
  }

  const credentials = data ?? [];

  // Pre-bucket by category so a missing category renders an empty state
  // rather than just disappearing — easier to spot data-quality issues
  // (e.g. a contractor with no OSHA on file at all).
  const grouped = CATEGORY_ORDER.reduce<
    Record<string, ContractorCredential[]>
  >((acc, cat) => {
    acc[cat] = credentials.filter((c) => c.category === cat);
    return acc;
  }, {});

  return (
    <Card className="worker-credentials" sx={{ mt: 2 }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={1.5}
        >
          <Typography variant="h5" fontWeight={700}>
            Credentials & certifications
          </Typography>
          {(isLoading || isFetching) && <CircularProgress size={18} />}
        </Stack>

        {isError && (
          <Typography color="error" fontSize={13}>
            Could not load credentials for this contractor.
          </Typography>
        )}

        {!isLoading && !isError && credentials.length === 0 && (
          <Typography color="text.secondary" fontSize={13}>
            This contractor has not added any credentials yet.
          </Typography>
        )}

        {credentials.length > 0 && (
          <Stack divider={<Divider flexItem />} spacing={1}>
            {CATEGORY_ORDER.filter((cat) => grouped[cat].length > 0).map(
              (cat) => (
                <Box key={cat} pt={1}>
                  <Typography
                    fontWeight={600}
                    fontSize={13}
                    color="text.secondary"
                    textTransform="uppercase"
                    letterSpacing={0.4}
                    mb={0.5}
                  >
                    {CATEGORY_LABELS[cat]}
                  </Typography>
                  <Stack spacing={0.75}>
                    {grouped[cat].map((cred) => (
                      <Stack
                        key={cred.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                      >
                        <Box>
                          <Typography fontSize={14} fontWeight={500}>
                            {cred.name}
                          </Typography>
                          <Typography fontSize={12} color="text.secondary">
                            {cred.issuer
                              ? `${cred.issuer} · `
                              : ''}
                            Expires {formatExpiry(cred.expirationDate)}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          color={STATUS_COLOR[cred.status]}
                          label={STATUS_LABEL[cred.status]}
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default CredentialsSection;
